import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useLogin } from '../api/hooks.js';
import { Button } from '../components/Button.js';
import { Field, TextInput } from '../components/Field.js';
import { Mark } from '../components/Mark.js';
import { ProblemNote } from '../components/Notes.js';

const LINK_MESSAGES: Record<string, string> = {
  expired: 'That sign-in link has expired. Request a new one.',
  consumed: 'That sign-in link was already used. Request a new one.',
  invalid: 'That sign-in link is not valid. Request a new one.',
  suspended: 'This account is suspended. Contact Dira.',
};

/** Wireframe §2: magic link, no passwords. */
export function Login() {
  const [params] = useSearchParams();
  const linkState = params.get('link');
  const [email, setEmail] = useState('');
  const login = useLogin();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    login.mutate(email.trim());
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-ground px-6">
      <div className="flex w-full max-w-[420px] flex-col gap-7 animate-settle">
        <div className="flex items-center gap-2.5 text-[18px] font-semibold tracking-[-0.01em]">
          <Mark />
          Dira
        </div>
        {login.isSuccess ? (
          <section className="flex flex-col gap-3 rounded-card border border-line bg-surface p-8 shadow-lift">
            <h1 className="m-0 text-[24px] font-semibold tracking-[-0.01em]">
              Check your email
            </h1>
            <p className="m-0 text-[14px] leading-relaxed text-mute">
              If{' '}
              <span className="font-semibold text-ink">{email.trim().toLowerCase()}</span>{' '}
              has access to Dira, a sign-in link is on its way. It expires in 15 minutes
              and works once.
            </p>
          </section>
        ) : (
          <form
            onSubmit={submit}
            className="flex flex-col gap-6 rounded-card border border-line bg-surface p-8 shadow-lift"
          >
            <h1 className="m-0 text-[24px] font-semibold tracking-[-0.01em]">
              Sign in to Dira
            </h1>
            {linkState && LINK_MESSAGES[linkState] ? (
              <div role="status" className="text-[14px] text-warn">
                {LINK_MESSAGES[linkState]}
              </div>
            ) : null}
            <Field id="email" label="Work email">
              <TextInput
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.co.zm"
              />
            </Field>
            {login.isError ? <ProblemNote error={login.error} /> : null}
            <Button type="submit" variant="primary" disabled={login.isPending}>
              {login.isPending ? 'Sending…' : 'Send sign-in link'}
            </Button>
            <p className="m-0 text-[13px] leading-relaxed text-mute">
              No passwords. The link expires in 15 minutes and works once. New
              organisation? Sign in with your work email — Dira verifies organisations
              before enabling records.
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
